// src/pages/motolivraison/categorie-detail.page.jsx
//
// Fiche d'une catégorie de commerces : identité, paramètres de commission,
// et surtout ce que la catégorie pèse réellement — boutiques rattachées,
// catalogue, commandes et chiffre d'affaires généré.
//
// Les chiffres sont agrégés à partir des boutiques de la catégorie
// (chaque Commerce porte ses propres compteurs `stats`), donc aucun nouvel
// endpoint n'est nécessaire côté serveur.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card, Row, Col, Tag, Descriptions, Button, message, Space, Breadcrumb,
  Avatar, Spin, Table, Empty, Typography, Progress, Switch,
} from 'antd';
import {
  AppstoreOutlined, ArrowLeftOutlined, RightOutlined, ShopOutlined,
  EyeOutlined, ContainerOutlined, DollarOutlined, PercentageOutlined,
  EnvironmentOutlined, InfoCircleOutlined, CheckCircleOutlined, CloseCircleOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate, Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { motolivraisonService } from '../../common/services';
import {
  LOCALE_TABLE, PAGINATION_TABLE, BTN_ACTION, COULEURS_ACTION, cmpTexte,
} from './table.helpers';
import './css/motolivraison-table.css';

const { Text } = Typography;
const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');
const fmtF = (n) => `${fmt(Math.round(Number(n || 0)))} F`;

const statusColor = (s) => ({ pending: 'orange', approved: 'green', suspended: 'red', rejected: 'default' })[s] || 'default';
const statusLabel = (s) => ({ pending: 'En attente', approved: 'Approuvé', suspended: 'Suspendu', rejected: 'Rejeté' })[s] || s;

const CategorieDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [categorie, setCategorie] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commerces, setCommerces] = useState([]);
  const [loadingCommerces, setLoadingCommerces] = useState(false);
  const [basculeEnCours, setBasculeEnCours] = useState(false);

  const fetchCategorie = useCallback(async () => {
    setLoading(true);
    try {
      const data = await motolivraisonService.getCategorieById(id);
      setCategorie(data?.categorie || data);
    } catch (e) {
      message.error(e?.message || 'Erreur lors du chargement de la catégorie');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchCategorie(); }, [fetchCategorie]);

  // Boutiques de la catégorie (filtre serveur categorieId)
  const fetchCommerces = useCallback(async () => {
    setLoadingCommerces(true);
    try {
      const r = await motolivraisonService.listCommerces({ categorieId: id, limit: 1000 });
      setCommerces(r?.commerces || []);
    } catch (e) {
      /* silencieux : le tableau affiche un état vide */
    } finally {
      setLoadingCommerces(false);
    }
  }, [id]);

  useEffect(() => { fetchCommerces(); }, [fetchCommerces]);

  const basculerActive = async () => {
    setBasculeEnCours(true);
    try {
      await motolivraisonService.toggleActiveCategorie(id);
      message.success(categorie.isActive ? 'Catégorie désactivée' : 'Catégorie activée');
      fetchCategorie();
    } catch (e) {
      message.error(e?.message || 'Erreur');
    } finally {
      setBasculeEnCours(false);
    }
  };

  // Agrégats calculés sur les boutiques de la catégorie
  const total = useMemo(() => commerces.reduce((acc, c) => ({
    articles: acc.articles + Number(c.stats?.totalArticles || 0),
    articlesActifs: acc.articlesActifs + Number(c.stats?.articlesActifs || 0),
    commandes: acc.commandes + Number(c.stats?.totalCommandes || 0),
    ca: acc.ca + Number(c.stats?.revenusBrut || 0),
    commission: acc.commission + Number(c.stats?.commissionTotale || 0),
  }), { articles: 0, articlesActifs: 0, commandes: 0, ca: 0, commission: 0 }), [commerces]);

  const parVille = useMemo(() => {
    const m = new Map();
    commerces.forEach(c => {
      const ville = c.cityId?.name || 'Ville inconnue';
      m.set(ville, (m.get(ville) || 0) + 1);
    });
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [commerces]);

  if (loading || !categorie) {
    return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
  }

  const couleur = categorie.couleur || '#E5A00D';

  const colonnes = [
    {
      title: 'Logo', key: 'logo', width: 80, align: 'center',
      render: (_, r) => <Avatar size={40} src={r.logo} icon={<ShopOutlined />} style={{ backgroundColor: couleur }} />,
    },
    {
      title: 'Boutique', dataIndex: 'nom', key: 'nom', width: 200, ellipsis: true,
      sorter: (a, b) => cmpTexte(a.nom, b.nom),
      render: (v) => <Text strong>{v}</Text>,
    },
    {
      title: 'Ville', key: 'ville', width: 130,
      sorter: (a, b) => cmpTexte(a.cityId?.name, b.cityId?.name),
      filters: parVille.map(([ville]) => ({ text: ville, value: ville })),
      onFilter: (value, r) => (r.cityId?.name || 'Ville inconnue') === value,
      render: (_, r) => r.cityId?.name || '—',
    },
    {
      title: 'Articles', key: 'articles', width: 110, align: 'center',
      sorter: (a, b) => (a.stats?.totalArticles || 0) - (b.stats?.totalArticles || 0),
      render: (_, r) => `${fmt(r.stats?.articlesActifs)} / ${fmt(r.stats?.totalArticles)}`,
    },
    {
      title: 'Commandes', key: 'commandes', width: 120, align: 'center',
      sorter: (a, b) => (a.stats?.totalCommandes || 0) - (b.stats?.totalCommandes || 0),
      render: (_, r) => fmt(r.stats?.totalCommandes),
    },
    {
      title: 'CA généré', key: 'ca', width: 140, align: 'right',
      sorter: (a, b) => (a.stats?.revenusBrut || 0) - (b.stats?.revenusBrut || 0),
      render: (_, r) => <strong>{fmtF(r.stats?.revenusBrut)}</strong>,
    },
    {
      title: 'Statut', dataIndex: 'status', key: 'status', width: 130,
      filters: ['pending', 'approved', 'suspended', 'rejected'].map(v => ({ text: statusLabel(v), value: v })),
      onFilter: (value, r) => r.status === value,
      render: (s) => <Tag color={statusColor(s)}>{statusLabel(s)}</Tag>,
    },
    {
      title: 'Actions', key: 'act', width: 130, align: 'center', fixed: 'right',
      render: (_, r) => (
        <Button
          size="small" icon={<EyeOutlined />} style={BTN_ACTION(COULEURS_ACTION.voir)}
          onClick={() => navigate(`/dashboard/motolivraison/commerces/${r._id}`)}
        >
          Détails
        </Button>
      ),
    },
  ];

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1" style={{ padding: '8px 24px' }}>
        <Breadcrumb separator={<RightOutlined />} style={{ marginBottom: 12 }}>
          <Breadcrumb.Item><Link to="/dashboard">Accueil</Link></Breadcrumb.Item>
          <Breadcrumb.Item>MotoLivraison</Breadcrumb.Item>
          <Breadcrumb.Item><Link to="/dashboard/motolivraison/categories">Catégories</Link></Breadcrumb.Item>
          <Breadcrumb.Item>{categorie.nom}</Breadcrumb.Item>
        </Breadcrumb>

        {/* ─── Bannière aux couleurs de la catégorie ───────────────────── */}
        <Card style={{ marginBottom: 16, overflow: 'hidden' }} styles={{ body: { padding: 0 } }}>
          <div
            style={{
              padding: '22px 24px',
              background: categorie.image
                ? `linear-gradient(135deg, ${couleur}f2, rgba(51,65,85,.85)), url(${categorie.image}) center/cover`
                : `linear-gradient(135deg, ${couleur}, #334155)`,
              color: '#fff',
            }}
          >
            <Row gutter={[16, 16]} align="middle" justify="space-between" wrap>
              <Col xs={24} lg={15}>
                <Space size={18} align="start" wrap={false}>
                  <Avatar
                    size={84}
                    src={categorie.image || undefined}
                    icon={<AppstoreOutlined />}
                    style={{ backgroundColor: couleur, border: '3px solid rgba(255,255,255,.85)', flexShrink: 0 }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <h2 style={{ color: '#fff', margin: 0, fontSize: 24, lineHeight: 1.2 }}>{categorie.nom}</h2>
                    <Space size={[6, 6]} wrap style={{ marginTop: 8 }}>
                      <Tag style={{ margin: 0 }}>{categorie.code}</Tag>
                      <Tag color={categorie.isActive ? 'green' : 'default'} style={{ margin: 0 }}>
                        {categorie.isActive ? 'Active' : 'Inactive'}
                      </Tag>
                      <Tag style={{ margin: 0 }}>Ordre d'affichage : {categorie.ordre ?? 0}</Tag>
                      <Tag style={{ margin: 0 }}>{commerces.length} boutique{commerces.length > 1 ? 's' : ''}</Tag>
                    </Space>
                    {categorie.description && (
                      <div style={{ marginTop: 10, fontSize: 13, opacity: 0.92, maxWidth: 640 }}>
                        {categorie.description}
                      </div>
                    )}
                  </div>
                </Space>
              </Col>

              <Col xs={24} lg={9} style={{ textAlign: 'right' }}>
                <Space wrap style={{ justifyContent: 'flex-end' }}>
                  <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>Retour</Button>
                  <Button
                    icon={<AppstoreOutlined />}
                    style={BTN_ACTION(COULEURS_ACTION.voir)}
                    onClick={() => navigate(`/dashboard/motolivraison/commerces?categorieCode=${categorie.code}`)}
                  >
                    Voir les boutiques
                  </Button>
                </Space>
              </Col>
            </Row>
          </div>

          {/* Ce que pèse la catégorie */}
          <Row style={{ borderTop: '1px solid #f0f0f0' }}>
            {[
              { label: 'Boutiques', valeur: fmt(commerces.length), couleur: '#334155', icone: <ShopOutlined /> },
              { label: 'Articles actifs', valeur: `${fmt(total.articlesActifs)} / ${fmt(total.articles)}`, couleur: '#52c41a', icone: <AppstoreOutlined /> },
              { label: 'Commandes', valeur: fmt(total.commandes), couleur: '#1677ff', icone: <ContainerOutlined /> },
              { label: 'CA généré', valeur: fmtF(total.ca), couleur: '#fa8c16', icone: <DollarOutlined /> },
            ].map((k, i) => (
              <Col xs={12} md={6} key={k.label} style={{ padding: '14px 18px', borderLeft: i ? '1px solid #f0f0f0' : 'none' }}>
                <Space size={10} align="center">
                  <span style={{ background: `${k.couleur}1a`, color: k.couleur, width: 34, height: 34, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {k.icone}
                  </span>
                  <div style={{ lineHeight: 1.25 }}>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>{k.label}</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: k.couleur }}>{k.valeur}</div>
                  </div>
                </Space>
              </Col>
            ))}
          </Row>
        </Card>

        <Row gutter={[16, 16]}>
          {/* ─── Colonne principale : les boutiques de la catégorie ────── */}
          <Col xs={24} xl={17}>
            <Card size="small" title={<Space><ShopOutlined />Boutiques de la catégorie ({commerces.length})</Space>}>
              <Table
                className="moto-table"
                rowKey="_id"
                size="middle"
                loading={loadingCommerces}
                dataSource={commerces}
                locale={{ ...LOCALE_TABLE, emptyText: <Empty description="Aucune boutique dans cette catégorie" /> }}
                pagination={PAGINATION_TABLE('boutique')}
                scroll={{ x: 1040 }}
                columns={colonnes}
              />
            </Card>
          </Col>

          {/* ─── Colonne latérale : paramétrage et répartition ─────────── */}
          <Col xs={24} xl={7}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <Card size="small" title={<Space><PercentageOutlined />Commission</Space>}>
                {categorie.commissionMode ? (
                  <>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#722ed1' }}>
                      {categorie.commissionValue}{categorie.commissionMode === 'percentage' ? ' %' : ' F'}
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Barème propre à la catégorie — il prime sur le paramètre global, mais une
                      boutique peut encore le surcharger.
                    </Text>
                  </>
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Aucun barème spécifique : les commandes utilisent la commission globale
                    du module (paramètres MotoLivraison).
                  </Text>
                )}
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #f0f0f0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: '#8c8c8c' }}>Commission encaissée</span>
                    <strong style={{ color: '#722ed1' }}>{fmtF(total.commission)}</strong>
                  </div>
                  {total.ca > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginTop: 4 }}>
                      <span style={{ color: '#8c8c8c' }}>Part du CA</span>
                      <span>{((total.commission / total.ca) * 100).toFixed(1)} %</span>
                    </div>
                  )}
                </div>
              </Card>

              <Card size="small" title={<Space><EnvironmentOutlined />Répartition par ville</Space>}>
                {parVille.length ? (
                  <Space direction="vertical" size={10} style={{ width: '100%' }}>
                    {parVille.map(([ville, n]) => (
                      <div key={ville}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                          <span>{ville}</span>
                          <strong>{n}</strong>
                        </div>
                        <Progress
                          percent={Math.round((n / commerces.length) * 100)}
                          showInfo={false} size="small" strokeColor={couleur}
                        />
                      </div>
                    ))}
                  </Space>
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>Aucune boutique rattachée.</Text>
                )}
              </Card>

              <Card size="small" title={<Space><InfoCircleOutlined />Paramètres</Space>}>
                <Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', width: 120 }}>
                  <Descriptions.Item label="Code">{categorie.code}</Descriptions.Item>
                  <Descriptions.Item label="Ordre">{categorie.ordre ?? 0}</Descriptions.Item>
                  <Descriptions.Item label="Couleur">
                    <Space size={6}>
                      <span style={{ width: 14, height: 14, borderRadius: 4, background: couleur, display: 'inline-block', border: '1px solid #d9d9d9' }} />
                      {couleur}
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label="Icône">{categorie.icone || <Text type="secondary">Aucune</Text>}</Descriptions.Item>
                  <Descriptions.Item label="Créée le">
                    {categorie.createdAt ? dayjs(categorie.createdAt).format('DD/MM/YYYY') : '—'}
                  </Descriptions.Item>
                </Descriptions>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, paddingTop: 10, borderTop: '1px dashed #f0f0f0' }}>
                  <span style={{ fontSize: 13 }}>Visible par les clients</span>
                  <Switch
                    checked={categorie.isActive}
                    loading={basculeEnCours}
                    onChange={basculerActive}
                    checkedChildren={<CheckCircleOutlined />}
                    unCheckedChildren={<CloseCircleOutlined />}
                  />
                </div>
              </Card>
            </Space>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default CategorieDetail;
